import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { StatFurniture, useStatsFurnitures } from "queries/StatsFurniture";
import "./Stats.css";

type RowData = Record<string, string | number | undefined> & {
  name?: string;
  parentName?: string;
  treeId?: string;
  erpId?: string;
  brandId?: string;
  country?: string;
  managerName?: string;
};

interface Column {
  title: string;
  field: string;
}

const updateDataTab = (
  tab: Record<string, RowData>,
  keyProperty: string,
  name: string,
  parentName?: string,
  values?: Record<string, string>
) => {
  if (!values) return;

  if (!tab[keyProperty]) {
    tab[keyProperty] = {
      ...values,
      name,
      parentName,
      treeId: keyProperty,
    };
    return;
  }

  for (const [key, value] of Object.entries(values)) {
    if (tab[keyProperty][key]) {
      tab[keyProperty][key] = Number(tab[keyProperty][key]) + parseInt(value, 10);
    } else {
      tab[keyProperty][key] = parseInt(value, 10);
    }
  }
};

const transformData = (sourceData: StatFurniture[]): RowData[] => {
  const data: RowData[] = [];
  const dataCountry: Record<string, RowData> = {};
  const dataBrand: Record<string, RowData> = {};
  const dataManager: Record<string, RowData> = {};

  sourceData.forEach((stat) => {
    const { erpId, name, brandId, country, managerName, ...rest } = stat;
    updateDataTab(dataCountry, country, country, undefined, rest);
    updateDataTab(dataBrand, `${country}-${brandId}`, brandId, country, rest);
    updateDataTab(dataManager, `${managerName}-${brandId}-${country}`, managerName, `${country}-${brandId}`, rest);

    data.push({ parentName: `${managerName}-${brandId}-${country}`, ...stat });
  });

  return data.concat(Object.values(dataCountry), Object.values(dataBrand), Object.values(dataManager));
};

const getColumns = (dataSource: StatFurniture[]): Column[] => {
  const columns: Column[] = [{ title: "", field: "name" }];

  if (dataSource.length > 0) {
    const { erpId, name, brandId, country, managerName, ...rest } = dataSource[0];
    Object.keys(rest).forEach((col) => columns.push({ title: col, field: col }));
  }

  return columns;
};

const getRowId = (row: RowData) => {
  if (row.treeId) return `tree:${row.treeId}`;
  return `leaf:${row.erpId || ""}:${row.name || ""}:${row.brandId || ""}:${row.country || ""}:${row.managerName || ""}`;
};

interface TreeRow {
  row: RowData;
  id: string;
  depth: number;
  children: TreeRow[];
}

const buildHierarchy = (rows: RowData[]) => {
  const nodes = new Map<string, TreeRow>();

  rows.forEach((row) => {
    const id = getRowId(row);
    nodes.set(id, { row, id, depth: 0, children: [] });
  });

  const roots: TreeRow[] = [];

  rows.forEach((row) => {
    const node = nodes.get(getRowId(row));
    if (!node) return;

    const parentRow = rows.find((candidate) =>
      candidate.treeId ? candidate.treeId === row.parentName : candidate.name === row.parentName
    );

    if (!parentRow) {
      roots.push(node);
      return;
    }

    const parentNode = nodes.get(getRowId(parentRow));
    if (!parentNode) {
      roots.push(node);
      return;
    }

    node.depth = parentNode.depth + 1;
    parentNode.children.push(node);
  });

  const flattened: TreeRow[] = [];
  const walk = (items: TreeRow[]) => {
    items.forEach((item) => {
      flattened.push(item);
      if (item.children.length > 0) walk(item.children);
    });
  };

  walk(roots);
  return flattened;
};

const StatsFurnitureTree = () => {
  const { data: dataSource = [], isLoading: isLoadingData } = useStatsFurnitures();

  if (isLoadingData) return null;

  const data = transformData(dataSource);
  const columns = getColumns(dataSource);
  const rows = buildHierarchy(data);

  return (
    <TableContainer sx={{ overflow: "auto" }}>
      <Table size="small" className="stat-furniture">
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <TableCell key={column.field} sx={{ fontSize: "12px", lineHeight: "16px" }}>
                {column.title}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(({ id, row, depth }) => (
            <TableRow key={id}>
              {columns.map((column) => {
                const isNameColumn = column.field === "name";
                const isEmphasized = !row.parentName || depth === 2;

                return (
                  <TableCell key={`${id}:${column.field}`} sx={{ fontSize: "12px" }}>
                    {isNameColumn ? (
                      <Box sx={{ pl: `${depth * 2}px` }}>
                        <Typography component="span" sx={{ fontSize: "inherit", fontWeight: isEmphasized ? 700 : 400 }}>
                          {row[column.field] ?? ""}
                        </Typography>
                      </Box>
                    ) : (
                      row[column.field] ?? ""
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default StatsFurnitureTree;
